/**
 * AttributionPanel Component
 *
 * Displays attribution graph summaries for a selected run.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { api, AttributionEdge, AttributionResponse } from '../services/api';
import DataBadge from './DataBadge';

interface RunOption {
  id: string;
  created_at?: string;
}

const AttributionPanel: React.FC = () => {
  const [runs, setRuns] = useState<RunOption[]>([]);
  const [selectedRun, setSelectedRun] = useState<string>('');
  const [attrib, setAttrib] = useState<AttributionResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchRuns = async () => {
    try {
      const list = await api.getRuns();
      const options = (Array.isArray(list) ? list : []).map((run: any) => ({
        id: run.id,
        created_at: run.created_at,
      }));
      setRuns(options);
      if (!selectedRun && options.length > 0) {
        setSelectedRun(options[0].id);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load runs');
    }
  };

  useEffect(() => {
    fetchRuns();
  }, []);

  useEffect(() => {
    if (!selectedRun) {
      setAttrib(null);
      return;
    }
    const loadAttrib = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await api.getRunAttribution(selectedRun);
        setAttrib(data);
      } catch (err) {
        setAttrib(null);
        setError(err instanceof Error ? err.message : 'Failed to load attribution');
      } finally {
        setLoading(false);
      }
    };
    loadAttrib();
  }, [selectedRun]);

  const aggregatedEdges = useMemo(() => {
    if (!attrib?.edges) return [];
    const bySource = new Map<string, number>();
    attrib.edges.forEach((edge: AttributionEdge) => {
      const current = bySource.get(edge.source) || 0;
      bySource.set(edge.source, current + edge.weight);
    });
    return Array.from(bySource.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);
  }, [attrib]);

  return (
    <div className="bg-gray-800 rounded-lg p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-semibold text-white">Attribution</h3>
          <DataBadge tier="MODELLED" />
        </div>
        <button
          onClick={fetchRuns}
          className="text-xs px-2 py-1 rounded bg-gray-700 hover:bg-gray-600 text-gray-200"
        >
          Refresh
        </button>
      </div>

      <div>
        <label className="block text-xs text-gray-400 mb-1">Run</label>
        <select
          value={selectedRun}
          onChange={(e) => setSelectedRun(e.target.value)}
          className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1.5 text-white text-sm"
        >
          {runs.length === 0 && <option value="">No runs available</option>}
          {runs.map((run) => (
            <option key={run.id} value={run.id}>
              {run.id}
            </option>
          ))}
        </select>
      </div>

      {loading && <div className="text-sm text-gray-400">Loading attribution...</div>}
      {error && <div className="text-sm text-red-400">{error}</div>}

      {attrib && !loading && (
        <div className="space-y-2 text-xs text-gray-300">
          <div>Harm Metric: {attrib.harm_metric || 'n/a'}</div>
          <div>Base Harm: {attrib.base_harm?.toFixed?.(1) ?? 'n/a'}</div>
          <div>Method: {attrib.method || 'n/a'}</div>

          <div className="mt-2">
            <div className="text-xs text-gray-400 mb-1">Top Contributors</div>
            {aggregatedEdges.length === 0 && (
              <div className="text-xs text-gray-500">No attribution edges available.</div>
            )}
            {aggregatedEdges.map(([source, weight]) => (
              <div key={source} className="flex justify-between">
                <span className="truncate max-w-[65%]">{source}</span>
                <span>{weight.toFixed(1)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AttributionPanel;
